/**
 * Service for managing hourly employees in local storage
 */

const HOURLY_EMPLOYEES_KEY = 'sweetgrass_hourly_employees';

export const hourlyEmployeesService = {
  /**
   * Get all hourly employees from local storage
   */
  getHourlyEmployees: () => {
    try {
      const stored = localStorage.getItem(HOURLY_EMPLOYEES_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading hourly employees:', error);
      return [];
    }
  },

  /**
   * Save hourly employees to local storage
   */
  saveHourlyEmployees: (employees) => {
    try {
      localStorage.setItem(HOURLY_EMPLOYEES_KEY, JSON.stringify(employees));
      return true;
    } catch (error) {
      console.error('Error saving hourly employees:', error);
      return false;
    }
  },

  /**
   * Add a new hourly employee
   */
  addHourlyEmployee: (employee) => {
    const employees = hourlyEmployeesService.getHourlyEmployees();
    const newEmployee = {
      id: Date.now().toString(),
      name: employee.name.trim(),
      job: employee.job.trim(),
      hourlyRate: parseFloat(employee.hourlyRate),
      createdAt: new Date().toISOString()
    };
    
    employees.push(newEmployee);
    return hourlyEmployeesService.saveHourlyEmployees(employees) ? newEmployee : null;
  },

  /**
   * Update an existing hourly employee
   */
  updateHourlyEmployee: (id, updates) => {
    const employees = hourlyEmployeesService.getHourlyEmployees();
    const index = employees.findIndex(employee => employee.id === id);
    
    if (index !== -1) {
      employees[index] = {
        ...employees[index],
        name: updates.name?.trim() || employees[index].name,
        job: updates.job?.trim() || employees[index].job,
        hourlyRate: updates.hourlyRate !== undefined ? parseFloat(updates.hourlyRate) : employees[index].hourlyRate,
        updatedAt: new Date().toISOString()
      };
      
      return hourlyEmployeesService.saveHourlyEmployees(employees) ? employees[index] : null;
    }
    
    return null;
  },

  /**
   * Delete a hourly employee
   */
  deleteHourlyEmployee: (id) => {
    const employees = hourlyEmployeesService.getHourlyEmployees();
    const filteredEmployees = employees.filter(employee => employee.id !== id);
    return hourlyEmployeesService.saveHourlyEmployees(filteredEmployees);
  },

  /**
   * Get a single hourly employee by ID
   */
  getHourlyEmployee: (id) => {
    const employees = hourlyEmployeesService.getHourlyEmployees();
    return employees.find(employee => employee.id === id) || null;
  },

  /**
   * Convert hourly employees to report expenses format with hours calculation
   */
  convertEmployeesToExpenses: (employeeHours, date = new Date()) => {
    const allEmployees = hourlyEmployeesService.getHourlyEmployees();
    
    return employeeHours.map(({ employeeId, hours }) => {
      const employee = allEmployees.find(emp => emp.id === employeeId);
      if (!employee) return null;
      
      const totalPay = employee.hourlyRate * parseFloat(hours);
      
      return {
        id: `hourly_${employee.id}_${Date.now()}`, // Unique ID for the report
        description: `${employee.name} (${employee.job}) - ${hours} hours @ $${employee.hourlyRate.toFixed(2)}/hr`,
        amount: totalPay,
        date: typeof date === 'string' ? date : format(date, 'yyyy-MM-dd'),
        isNew: true,
        isFromHourlyEmployee: true,
        employeeId: employee.id,
        hours: parseFloat(hours),
        hourlyRate: employee.hourlyRate
      };
    }).filter(Boolean);
  },

  /**
   * Calculate total pay for an employee based on hours
   */
  calculatePay: (employeeId, hours) => {
    const employee = hourlyEmployeesService.getHourlyEmployee(employeeId);
    if (!employee || !hours) return 0;
    return employee.hourlyRate * parseFloat(hours);
  },

  /**
   * Migrate data from old laundry employees service (runs only once)
   */
  migrateLaundryEmployees: () => {
    try {
      // Check if migration has already been done
      const migrationFlag = localStorage.getItem('sweetgrass_laundry_migration_done');
      if (migrationFlag) {
        console.log('Migration already completed, skipping...');
        return false;
      }

      // Check if hourly employees already exist
      const existingHourlyEmployees = hourlyEmployeesService.getHourlyEmployees();
      if (existingHourlyEmployees.length > 0) {
        console.log('Hourly employees already exist, skipping migration...');
        localStorage.setItem('sweetgrass_laundry_migration_done', 'true');
        return false;
      }

      const laundryData = localStorage.getItem('sweetgrass_laundry_employees');
      if (laundryData) {
        const laundryEmployees = JSON.parse(laundryData);
        const migratedEmployees = laundryEmployees.map(emp => ({
          ...emp,
          job: emp.job || 'Laundry' // Use existing job or default to 'Laundry'
        }));
        
        if (hourlyEmployeesService.saveHourlyEmployees(migratedEmployees)) {
          localStorage.setItem('sweetgrass_laundry_migration_done', 'true');
          console.log('Successfully migrated laundry employees to hourly employees');
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Error migrating laundry employees:', error);
      return false;
    }
  }
};

// Helper function for date formatting
const format = (date, formatStr) => {
  if (formatStr === 'yyyy-MM-dd') {
    return date.toISOString().split('T')[0];
  }
  return date.toLocaleDateString();
};
